import { Control, Controller, RegisterOptions } from 'react-hook-form'

import { cn } from '@/lib/utils'

import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

interface SelectInputProps {
  control?: Control<any>
  name: string
  label?: string
  placeholder?: string
  options: { label: string; value: string }[]
  className?: string
  disabled?: boolean
  rules?: RegisterOptions
  value?: string
  onChange?: (value: string) => void
}

export const SelectInput = ({
  control,
  name,
  label,
  placeholder = 'Select',
  options,
  className,
  disabled,
  rules,
  value,
  onChange,
}: SelectInputProps) => {
  const labelNode = label && (
    <Label htmlFor={name} className="text-sm text-ink">{label}</Label>
  )

  const optionNodes = options.map((option) => (
    <SelectItem key={option.value} value={option.value}>
      {option.label}
    </SelectItem>
  ))

  // Uncontrolled mode: no react-hook-form control provided (e.g. filter dropdowns)
  if (!control) {
    return (
      <div className={cn('space-y-1.5', className)}>
        {labelNode}
        <Select value={value} onValueChange={(val) => onChange?.(val)} disabled={disabled}>
          <SelectTrigger>
            <SelectValue placeholder={placeholder} />
          </SelectTrigger>
          <SelectContent>{optionNodes}</SelectContent>
        </Select>
      </div>
    )
  }

  return (
    <div className={cn('space-y-1.5', className)}>
      {labelNode}
      <Controller
        control={control}
        name={name}
        rules={rules}
        render={({ field, fieldState: { error } }) => (
          <>
            <Select
              onValueChange={(val) => {
                const nextValue = val === 'all' ? null : val
                field.onChange(nextValue)
                onChange?.(nextValue as string)
              }}
              value={field.value ?? 'all'}
              disabled={disabled}
            >
              <SelectTrigger className={cn(error && 'border-destructive')}>
                <SelectValue placeholder={placeholder} />
              </SelectTrigger>
              <SelectContent>{optionNodes}</SelectContent>
            </Select>
            {error && <p className="text-sm text-destructive">{error.message}</p>}
          </>
        )}
      />
    </div>
  )
}
